import React, { useState } from 'react';
import { Check, X } from 'lucide-react';

import { alternarRol, normalizarRol, unirRoles } from '../../lib/roles';
import './SelectorDeRoles.css';

/**
 * Los roles de una persona, como etiquetas que se prenden y se apagan.
 *
 * Era un campo de texto donde se escribía el puesto a mano, uno solo por
 * persona. Acá se toca la etiqueta que ya existe —la que se usó para el
 * cardiólogo anterior— y sólo se escribe cuando el rol es nuevo. Se pueden
 * prender varias a la vez, que es el caso que antes no entraba: el que además
 * de cardiólogo es nutricionista.
 *
 * El catálogo lo arma quien monta este componente (lib/roles.js): son los roles
 * que el equipo ya tiene puestos más los sugeridos del rubro. No hay una lista
 * de etiquetas guardada aparte.
 *
 *
 * ESCRIBIR UN ROL NUEVO LO PRENDE, NO LO DEJA ESCRITO
 *
 * Al confirmar, el texto se convierte en etiqueta prendida y el campo se vacía.
 * Es lo que evita el error más caro de un campo así: escribir el rol, no
 * confirmarlo, y guardar la persona con el rol perdido porque quedaba a medio
 * tipear. Acá lo que se ve prendido es exactamente lo que se va a guardar.
 */
/**
 * `sugeridos` son los roles que propone el rubro. No se pueden borrar: no los
 * creó nadie de este negocio, son la ayuda para empezar y volverían igual en el
 * próximo render, porque salen de una lista fija (data/rubros.js). Borrable es
 * lo que el negocio agregó.
 *
 * `onBorrar` es opcional. Sin él no aparece ninguna cruz — sirve para montar
 * este selector en una pantalla donde no se pueda tocar el catálogo.
 */
const SelectorDeRoles = ({
  value = [],
  onChange,
  catalogo = [],
  sugeridos = [],
  onBorrar,
  usosDelRol,
  id = 'roles',
}) => {
  const [texto, setTexto] = useState('');
  /**
   * El rol que está esperando confirmación, o `null`.
   *
   * La pregunta se hace en la misma pastilla y no con un cartel: un `confirm()`
   * frena la página entera y no se puede estilar —por eso se sacó del resto del
   * onboarding—, y un modal para borrar una etiqueta es más ceremonia que el
   * borrado. Acá la pastilla se abre en el lugar, dice a cuánta gente afecta y
   * se cierra sola si tocás cualquier otra.
   */
  const [porBorrar, setPorBorrar] = useState(null);

  // Las prendidas van SIEMPRE, aunque no estén en el catálogo. Pasa al editar a
  // alguien cuyo rol ya no usa nadie más: si sólo se dibujara el catálogo, esa
  // persona abriría el formulario con su propio rol invisible y guardarla lo
  // borraría sin que nadie lo haya tocado.
  const etiquetas = unirRoles(value, catalogo);

  const estaPrendida = (rol) => value.some((r) => normalizarRol(r) === normalizarRol(rol));

  const esSugerido = (rol) =>
    sugeridos.some((s) => normalizarRol(s) === normalizarRol(rol));

  const agregarEscrito = () => {
    const nuevo = texto.trim();
    if (!nuevo) return;

    // Si ya está prendida no se apaga: el que escribe un rol lo está agregando,
    // no alternándolo. Apagarlo acá sería lo contrario de lo que pidió.
    if (!estaPrendida(nuevo)) onChange(alternarRol(value, nuevo));
    setTexto('');
  };

  const alTeclear = (evento) => {
    // La coma también confirma: es como se escriben las listas fuera de una
    // computadora, y quien viene de escribir "Cardiólogo, nutricionista" en un
    // campo de texto la va a usar sin pensarlo.
    if (evento.key === 'Enter' || evento.key === ',') {
      // `preventDefault` en Enter para que no se envíe el formulario entero, que
      // es lo que hace un Enter dentro de un <form> con un solo campo de texto.
      evento.preventDefault();
      agregarEscrito();
      return;
    }

    // Borrar hacia atrás con el campo vacío saca la última prendida. Es el gesto
    // que ya tiene cualquier campo de etiquetas.
    if (evento.key === 'Backspace' && !texto && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="selector-de-roles">
      {etiquetas.length > 0 && (
        <ul className="selector-de-roles__etiquetas">
          {etiquetas.map((rol) => {
            const prendida = estaPrendida(rol);
            /**
             * La cruz aparece SÓLO en las etiquetas apagadas.
             *
             * Prendida, la pastilla tenía dos acciones que se parecen y hacen
             * cosas muy distintas: el cuerpo se la saca a esta persona y la cruz
             * la borra de todo el equipo. Un click al lado del otro, sin forma
             * de distinguirlos al pasar rápido. Apagada no hay confusión posible
             * —esa etiqueta ya no es de esta persona— y el gesto queda en el
             * orden natural: primero se la sacás, después la borrás si no la
             * usás más.
             */
            const sePuedeBorrar = Boolean(onBorrar) && !esSugerido(rol) && !prendida;

            // Preguntando por ESTA: la pastilla se reemplaza entera. No se
            // agrega la pregunta al lado para que la fila no cambie de alto y
            // el resto de las etiquetas no se muevan de lugar mientras decidís.
            if (porBorrar && normalizarRol(porBorrar) === normalizarRol(rol)) {
              const usos = usosDelRol ? usosDelRol(rol) : 0;

              return (
                <li key={normalizarRol(rol)} className="selector-de-roles__item se-pregunta">
                  <span className="selector-de-roles__pregunta">
                    {usos > 0
                      ? `¿Borrar "${rol}"? Se lo saca a ${usos} ${usos === 1 ? 'persona' : 'personas'}`
                      : `¿Borrar "${rol}"?`}
                  </span>
                  <button
                    type="button"
                    className="selector-de-roles__confirmar"
                    onClick={() => {
                      onBorrar(rol);
                      setPorBorrar(null);
                    }}
                    autoFocus
                  >
                    Sí, borrar
                  </button>
                  <button
                    type="button"
                    className="selector-de-roles__cancelar"
                    onClick={() => setPorBorrar(null)}
                  >
                    No
                  </button>
                </li>
              );
            }

            return (
              <li
                key={normalizarRol(rol)}
                className={`selector-de-roles__item${prendida ? ' esta-prendida' : ''}`}
              >
                {/* Dos controles y no uno: el cuerpo se la pone o se la saca a
                    ESTA persona, la cruz borra la etiqueta de todo el negocio.
                    Son cosas muy distintas y por eso son dos botones separados
                    —antes el ícono de adentro era parte del mismo botón y no
                    había forma de ofrecer las dos acciones sin que una se
                    confundiera con la otra—. */}
                <button
                  type="button"
                  className="selector-de-roles__etiqueta"
                  onClick={() => onChange(alternarRol(value, rol))}
                  aria-pressed={prendida}
                >
                  {prendida && <Check className="selector-de-roles__icono" aria-hidden="true" />}
                  {rol}
                </button>

                {sePuedeBorrar && (
                  <button
                    type="button"
                    className="selector-de-roles__borrar"
                    onClick={() => setPorBorrar(rol)}
                    aria-label={`Borrar el rol ${rol}`}
                    title={`Borrar "${rol}" de todo el equipo`}
                  >
                    <X className="selector-de-roles__icono" aria-hidden="true" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="selector-de-roles__alta">
        <input
          id={id}
          type="text"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={alTeclear}
          // Al salir del campo se confirma lo escrito. Sin esto, escribir el rol
          // y tocar directamente "Guardar" lo perdía: el click saca el foco
          // antes de que nadie haya apretado Enter.
          onBlur={agregarEscrito}
          placeholder={etiquetas.length > 0 ? 'Agregar otro rol…' : 'Ej: Cardiólogo'}
          autoComplete="off"
        />
        <button
          type="button"
          className="selector-de-roles__agregar"
          onClick={agregarEscrito}
          disabled={!texto.trim()}
        >
          Agregar
        </button>
      </div>

      <p className="selector-de-roles__ayuda">
        Tocá un rol para ponérselo o sacárselo. Podés ponerle más de uno.
        {onBorrar && ' La × de un rol apagado lo borra de todo el equipo.'}
      </p>
    </div>
  );
};

export default SelectorDeRoles;
